// "Agent commands" card on a LAN event page: the agent version of every machine of the event
// (vs the panel's agent version) and every flux-agent command - install (with its options),
// update, uninstall, status / logs - each with a copy button. Compact by default; "Show all
// commands" expands it (versions + Install / Update / Uninstall / Check & logs tabs). With `installCommand` (the event's real command, token included) the
// install lines are ready to paste; without it (ended / expired) they show <TOKEN>.
// Linux / Windows switch: Windows = admin PowerShell, install.ps1, RCON only.
// Commands must match agent/src/cli.ts + server/services/lan/install.sh + install.ps1.
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CopyButton } from '../ext/extUi';

/** "1.10.0" > "1.9.3" */
export const isNewer = (a, b) => {
  const pa = String(a).split('.').map(Number), pb = String(b).split('.').map(Number);
  for (let i = 0; i < Math.max(pa.length, pb.length); i++) { const x = pa[i] || 0, y = pb[i] || 0; if (x !== y) return x > y; }
  return false;
};

/** Machine runs Windows (agent reports e.g. "Windows 11 Pro (10.0.26100)"). */
export const isWindowsMachine = (m) => /^windows/i.test((m && m.os) || '');

/** Linux / Windows segmented switch (styles: lan.css .lan-seg). */
export function OsSwitch({ value, onChange }) {
  const { t } = useTranslation();
  const opts = [['linux', t('lanCmd.osLinux', 'Linux (Ubuntu/Debian)')], ['windows', t('lanCmd.osWindows', 'Windows')]];
  return (
    <div role="tablist" className="lan-seg">
      {opts.map(([k, label]) => (
        <button key={k} type="button" role="tab" aria-selected={value === k} onClick={() => onChange(k)}>{label}</button>
      ))}
    </div>
  );
}

export default function AgentCommands({ installCommand, installCommandWindows, machines = [], latest }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState('install');
  const [os, setOs] = useState('linux');
  const base = installCommand || `curl -fsSL ${window.location.origin}/agent/install.sh | sudo bash -s -- <TOKEN>`;
  const winBase = installCommandWindows || `[Net.ServicePointManager]::SecurityProtocol='Tls12'; & ([scriptblock]::Create((irm ${window.location.origin}/agent/install.ps1))) -Token <TOKEN>`;

  // machines that run an agent (uninstalled / rejected ones don't)
  const live = machines.filter(m => m.status === 'approved' || m.status === 'pending');
  const behind = live.filter(m => m.version && latest && isNewer(latest, m.version));
  const summary = !live.length
    ? t('lanCmd.noMachines', 'No machines yet')
    : behind.length
      ? t('lanCmd.someBehind', '{{n}} of {{total}} machines need an update', { n: behind.length, total: live.length })
      : t('lanCmd.allCurrent', 'All {{total}} machines up to date', { total: live.length });

  const groups = [
    { key: 'install', title: t('lanCmd.tabInstall', 'Install'), rows: [
      { cmd: base, text: t('lanCmd.install', 'Install - asks RCON only or Full access. Run as root on every venue server machine (Ubuntu/Debian), CS2 servers started first.') },
      { cmd: `${base} --mode rcon`, text: t('lanCmd.installRcon', 'RCON only - commands and logs go through the agent; the panel cannot reach anything else on the machine.') },
      { cmd: `${base} --mode full`, text: t('lanCmd.installFull', 'Full access - adds a WireGuard tunnel so the panel reaches the machine like an online server (SFTP/FTP, LinuxGSM).') },
      { cmd: `${base} --name "Stage PC 1"`, text: t('lanCmd.installName', 'Set the machine name shown in the panel. Run it again on an installed machine to rename it.') },
      { cmd: `${base} --user cs2`, text: t('lanCmd.installUser', 'The Linux user that runs the CS2 servers, if the agent cannot find them by itself.') },
      { cmd: `${base} --log-port 27500`, text: t('lanCmd.installLogPort', 'Local port for the CS2 log listener (default 27500), if that one is taken.') },
    ] },
    { key: 'update', title: t('lanCmd.tabUpdate', 'Update'), rows: [
      { cmd: 'sudo flux-agent update', text: t('lanCmd.update', 'Update to the agent version of this panel. Keeps the name, mode and servers; does nothing if it is already current. The event page shows "Update available".') },
      { cmd: 'sudo flux-agent update --force', text: t('lanCmd.updateForce', 'Reinstall even when it is already current (repairs a broken install).') },
    ] },
    { key: 'uninstall', title: t('lanCmd.tabUninstall', 'Uninstall'), rows: [
      { cmd: 'sudo flux-agent uninstall', text: t('lanCmd.uninstall', 'Removes the agent, its keys, data and tunnel. The panel shows the machine as Uninstalled and it cannot reconnect. CS2 servers are not touched.') },
      { cmd: 'sudo flux-agent uninstall --yes', text: t('lanCmd.uninstallYes', 'The same without the confirmation question (scripts, no terminal).') },
    ] },
    { key: 'check', title: t('lanCmd.tabCheck', 'Check & logs'), rows: [
      { cmd: 'sudo flux-agent status', text: t('lanCmd.status', 'Panel address, machine id, mode, log port and the servers it manages.') },
      { cmd: 'sudo flux-agent scan', text: t('lanCmd.scan', 'Lists the CS2 servers running on this machine and whether their RCON password was found.') },
      { cmd: 'sudo journalctl -u flux-agent -f', text: t('lanCmd.logs', 'Live agent log (connection, RCON, errors). Ctrl+C to stop.') },
      { cmd: 'sudo systemctl restart flux-agent', text: t('lanCmd.restart', 'Restart the agent.') },
      { cmd: 'flux-agent version', text: t('lanCmd.version', 'Installed agent version.') },
      { cmd: 'sudo wg show flux0', text: t('lanCmd.tunnel', 'Full access only: tunnel status and the last handshake with the panel.') },
    ] },
  ];

  const winLog = 'Get-Content "$env:ProgramData\\FluxAgent\\agent.log" -Wait -Tail 50';
  const winGroups = [
    { key: 'install', title: t('lanCmd.tabInstall', 'Install'), rows: [
      { cmd: winBase, text: t('lanCmd.winInstall', 'Install - paste into PowerShell opened as Administrator on every venue server machine (Windows 10/11 or Server, 64-bit), CS2 servers started first. RCON only: commands and logs go through the agent.') },
      { cmd: `${winBase} -Name "Stage PC 1"`, text: t('lanCmd.installName', 'Set the machine name shown in the panel. Run it again on an installed machine to rename it.') },
      { cmd: `${winBase} -LogPort 27500`, text: t('lanCmd.installLogPort', 'Local port for the CS2 log listener (default 27500), if that one is taken.') },
    ] },
    { key: 'update', title: t('lanCmd.tabUpdate', 'Update'), rows: [
      { cmd: 'flux-agent update', text: t('lanCmd.update', 'Update to the agent version of this panel. Keeps the name, mode and servers; does nothing if it is already current. The event page shows "Update available".') },
      { cmd: 'flux-agent update --force', text: t('lanCmd.updateForce', 'Reinstall even when it is already current (repairs a broken install).') },
    ] },
    { key: 'uninstall', title: t('lanCmd.tabUninstall', 'Uninstall'), rows: [
      { cmd: 'flux-agent uninstall', text: t('lanCmd.winUninstall', 'Removes the agent, its scheduled task, keys and data. The panel shows the machine as Uninstalled and it cannot reconnect. CS2 servers are not touched.') },
      { cmd: 'flux-agent uninstall --yes', text: t('lanCmd.uninstallYes', 'The same without the confirmation question (scripts, no terminal).') },
    ] },
    { key: 'check', title: t('lanCmd.tabCheck', 'Check & logs'), rows: [
      { cmd: 'flux-agent status', text: t('lanCmd.status', 'Panel address, machine id, mode, log port and the servers it manages.') },
      { cmd: 'flux-agent scan', text: t('lanCmd.scan', 'Lists the CS2 servers running on this machine and whether their RCON password was found.') },
      { cmd: winLog, text: t('lanCmd.logs', 'Live agent log (connection, RCON, errors). Ctrl+C to stop.') },
      { cmd: 'Stop-ScheduledTask FluxAgent; Start-ScheduledTask FluxAgent', text: t('lanCmd.restart', 'Restart the agent.') },
      { cmd: 'flux-agent version', text: t('lanCmd.version', 'Installed agent version.') },
    ] },
  ];
  const shown = os === 'windows' ? winGroups : groups;

  return (
    <section className="lan-block">
      {/* compact header: title, version summary, expand */}
      <div className="lan-block-head" style={{ marginBottom: 0 }}>
        <h2 className="lan-h2">{t('lanCmd.title', 'Agent commands')}</h2>
        {latest && <span className="lan-tag info">{t('lanCmd.panelVersion', 'Panel agent v{{v}}', { v: latest })}</span>}
        <span className={`lan-tag${behind.length ? ' warn' : live.length ? ' ok' : ''}`}>{summary}</span>
        <span className="lan-spacer" />
        <button type="button" className="lan-btn lan-expand" onClick={() => setOpen(o => !o)} aria-expanded={open}>
          {open ? t('lanCmd.hide', 'Hide commands') : t('lanCmd.showAll', 'Show all commands')}
          <span aria-hidden="true">▸</span>
        </button>
      </div>

      {open && (
        <div style={{ marginTop: '1rem' }}>
          {/* versions per machine */}
          {live.length > 0 && (
            <div style={{ marginBottom: '1.25rem' }}>
              <div className="lan-label" style={{ marginBottom: '0.35rem' }}>{t('lanCmd.versions', 'Agent version per machine')}</div>
              <div className="lan-versions">
                {live.map(m => {
                  const old = m.version && latest && isNewer(latest, m.version);
                  return (
                    <div key={m.id} className={`lan-ver${old ? ' old' : ''}${m.online ? '' : ' offline'}`} title={m.online ? t('lan.online', 'Online') : t('lan.offline', 'Offline')}>
                      <span className="lan-ver-name">{m.name || m.hostname || m.uid}</span>
                      <code>{m.version ? `v${m.version}` : '-'}</code>
                      {old && <span className="lan-tag warn">{t('lanCmd.behind', 'update')}</span>}
                    </div>
                  );
                })}
              </div>
              {behind.length > 0 && (
                <p className="lan-muted" style={{ margin: '0.4rem 0 0' }}>
                  {t('lanCmd.updateHint2', 'Run the update command on the machines marked "update" (see Update below; Linux: sudo flux-agent update, Windows: flux-agent update as Administrator).')}
                </p>
              )}
            </div>
          )}

          <div style={{ marginBottom: '0.75rem' }}><OsSwitch value={os} onChange={setOs} /></div>
          <p className="lan-sub">
            {os === 'windows'
              ? (installCommandWindows
                ? t('lanCmd.winHintEvent', 'Run these on the venue machine in PowerShell opened as Administrator. The install lines already contain this event\'s token.')
                : t('lanCmd.winHintNoToken', 'Run these on the venue machine in PowerShell opened as Administrator. This event has no valid install command (ended or expired) - reopen it or regenerate the command to fill in <TOKEN>. Update, uninstall and checks work without it.'))
              : (installCommand
                ? t('lanCmd.hintEvent', 'Run these on the venue machine (Linux terminal / SSH). The install lines already contain this event\'s token.')
                : t('lanCmd.hintNoToken', 'Run these on the venue machine (Linux terminal / SSH). This event has no valid install command (ended or expired) - reopen it or regenerate the command to fill in <TOKEN>. Update, uninstall and checks work without it.'))}
          </p>

          {/* Install / Update / Uninstall / Check & logs side by side, one group shown at a time */}
          <div role="tablist" className="lan-seg" style={{ marginBottom: '1rem' }}>
            {shown.map(g => (
              <button key={g.key} type="button" role="tab" aria-selected={tab === g.key} onClick={() => setTab(g.key)}>{g.title}</button>
            ))}
          </div>
          <div className="lan-cmds">
            {(shown.find(g => g.key === tab) || shown[0]).rows.map(({ cmd, text }) => (
              <div key={cmd} className="lan-cmd">
                <p>{text}</p>
                <div className="lan-code-row">
                  <code className="lan-code">{cmd}</code>
                  <CopyButton text={cmd} />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </section>
  );
}
